<template>
  <div v-if="isVisible" class="edit-overlay">
    <div class="edit-overlay-backdrop" @click="closeOverlay"></div>
    <div class="edit-overlay-content">
      <div class="edit-header">
        <h2>Add New Metering Point</h2>
        <button class="close-button" @click="closeOverlay">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div class="form-container">
        <div v-if="isSubmitting" class="loading-overlay">
          <div class="loading-spinner"></div>
          <span>Creating metering point...</span>
        </div>

        <div class="form-content">
          <!-- Metering Point Information Section -->
          <div class="form-section">
            <h3 class="section-title">Metering Point Information</h3>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">MPID</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="formData.mpid"
                    required
                    placeholder="Enter MPID"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Customer Name</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="formData.customerFName"
                    required
                    placeholder="Enter customer name"
                />
              </div>
            </div>
          </div>

          <!-- Address Section -->
          <div class="form-section">
            <h3 class="section-title">Address Information</h3>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Street</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="formData.street"
                    required
                    placeholder="Enter street address"
                    @blur="onAddressFieldChange"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">City</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="formData.city"
                    required
                    placeholder="Enter city"
                    @blur="onAddressFieldChange"
                />
              </div>

              <div class="form-group">
                <label class="form-label">Zip Code</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="formData.zipCode"
                    placeholder="Enter zip code"
                    @blur="onAddressFieldChange"
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Country</label>
                <input
                    type="text"
                    class="form-input"
                    v-model="formData.country"
                    placeholder="Enter country"
                    @blur="onAddressFieldChange"
                />
              </div>
            </div>
          </div>

          <!-- Location Section -->
          <div class="form-section">
            <h3 class="section-title">Geographic Location</h3>

            <!-- Auto-Geocoding Toggle -->
            <div class="form-group">
              <div class="geocoding-toggle-container">
                <div class="toggle-wrapper">
                  <span class="toggle-text">Auto-geocoding</span>
                  <label class="toggle-switch">
                    <input
                        type="checkbox"
                        v-model="autoGeocoding"
                        @change="onAutoGeocodingToggle"
                        class="toggle-checkbox"
                    />
                    <span class="toggle-slider"></span>
                  </label>
                  <div v-if="geocodingInProgress" class="loading-indicator">
                    <svg class="loading-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <circle cx="12" cy="12" r="10"/>
                    </svg>
                    <span>Geocoding...</span>
                  </div>
                </div>
                <div class="geocoding-info">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                  </svg>
                  <span>{{ autoGeocoding ? 'Coordinates will be calculated automatically from address' : 'Enter coordinates manually or enable auto-geocoding' }}</span>
                </div>
              </div>
            </div>

            <!-- Geocoding Status Messages -->
            <div v-if="geocodingMessage" class="geocoding-message" :class="geocodingMessageType">
              <svg v-if="geocodingMessageType === 'success'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20,6 9,17 4,12"></polyline>
              </svg>
              <svg v-else-if="geocodingMessageType === 'error'" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="15" y1="9" x2="9" y2="15"></line>
                <line x1="9" y1="9" x2="15" y2="15"></line>
              </svg>
              <span>{{ geocodingMessage }}</span>
            </div>

            <div class="form-row">
              <div class="form-group">
                <label class="form-label">Latitude</label>
                <input
                    type="number"
                    class="form-input"
                    :class="{ 'geocoded': wasGeocoded, 'disabled': autoGeocoding }"
                    v-model.number="formData.lat"
                    placeholder="Enter latitude"
                    step="0.000001"
                    :disabled="autoGeocoding"
                />
                <small class="field-hint">{{ autoGeocoding ? 'Auto-calculated from address' : 'Leave empty for default (0)' }}</small>
              </div>

              <div class="form-group">
                <label class="form-label">Longitude</label>
                <input
                    type="number"
                    class="form-input"
                    :class="{ 'geocoded': wasGeocoded, 'disabled': autoGeocoding }"
                    v-model.number="formData.lon"
                    placeholder="Enter longitude"
                    step="0.000001"
                    :disabled="autoGeocoding"
                />
                <small class="field-hint">{{ autoGeocoding ? 'Auto-calculated from address' : 'Leave empty for default (0)' }}</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Action buttons -->
      <div class="edit-actions">
        <button class="action-button" @click="closeOverlay">Cancel</button>
        <button
            class="action-button submit-button"
            @click="submitForm"
            :disabled="isSubmitting || !formIsValid"
        >
          Create Metering Point
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import MpidApiService from '@/services/MpidApiService';

export default {
  name: 'AddMpidOverlay',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isSubmitting: false,
      autoGeocoding: true, // Enable auto-geocoding by default for new entries
      geocodingInProgress: false,
      geocodingDebounceTimer: null,
      geocodingMessage: '',
      geocodingMessageType: 'info', // 'info', 'success', 'error'
      wasGeocoded: false,
      formData: {
        mpid: '',
        street: '',
        city: '',
        zipCode: '',
        country: '',
        customerFName: '',
        lat: null,
        lon: null
      }
    };
  },
  computed: {
    formIsValid() {
      return !!(this.formData.mpid.trim() &&
          this.formData.street.trim() &&
          this.formData.city.trim() &&
          this.formData.customerFName.trim());
    },
    canGeocode() {
      return this.formData.street.trim() !== '' && this.formData.city.trim() !== '';
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden';
        this.resetForm();
      } else {
        document.body.style.overflow = '';
      }
    }
  },
  methods: {
    resetForm() {
      this.formData = {
        mpid: '',
        street: '',
        city: '',
        zipCode: '',
        country: '',
        customerFName: '',
        lat: null,
        lon: null
      };
      this.isSubmitting = false;
      this.autoGeocoding = true; // Reset to default enabled state
      this.clearGeocodingMessage();
      this.wasGeocoded = false;
    },

    onAutoGeocodingToggle() {
      if (this.autoGeocoding) {
        // When enabling auto-geocoding, trigger geocoding if address is available
        if (this.canGeocode) {
          // Debounce to avoid instant coordinate change on toggle
          clearTimeout(this.geocodingDebounceTimer);
          this.geocodingDebounceTimer = setTimeout(() => {
            this.performAutoGeocoding();
          }, 1000); // wait 1s after toggle
        }
      } else {
        // When disabling auto-geocoding, clear any geocoding messages
        this.clearGeocodingMessage();
        this.wasGeocoded = false;
      }
    },

    onAddressFieldChange() {
      // Clear geocoding message when address fields change
      if (this.geocodingMessage && this.geocodingMessageType === 'success') {
        this.clearGeocodingMessage();
        this.wasGeocoded = false;
      }

      // If auto-geocoding is enabled, automatically geocode when address changes
      if (this.autoGeocoding && this.canGeocode) {
        // Debounce the geocoding to avoid too many requests
        clearTimeout(this.geocodingDebounceTimer);
        this.geocodingDebounceTimer = setTimeout(() => {
          this.performAutoGeocoding();
        }, 1000); // Wait 1 second after user stops typing
      }
    },

    clearGeocodingMessage() {
      this.geocodingMessage = '';
      this.geocodingMessageType = 'info';
    },

    async performAutoGeocoding() {
      if (!this.canGeocode || this.geocodingInProgress) {
        return;
      }

      this.geocodingInProgress = true;
      this.clearGeocodingMessage();

      try {
        const addressData = {
          street: this.formData.street.trim(),
          city: this.formData.city.trim(),
          zip_code: this.formData.zipCode?.trim() || null,
          country: this.formData.country?.trim() || null
        };

        // Remove null/empty values
        Object.keys(addressData).forEach(key => {
          if (addressData[key] === null || addressData[key] === '') {
            delete addressData[key];
          }
        });

        const response = await MpidApiService.geocodeAddress(addressData);

        // Handle the new backend response format
        if (response && response.lat !== undefined && response.lon !== undefined) {
          this.formData.lat = parseFloat(response.lat);
          this.formData.lon = parseFloat(response.lon);
          this.wasGeocoded = true;

          // Create a concise success message
          let message = `Coordinates: ${response.lat.toFixed(6)}, ${response.lon.toFixed(6)}`;

          if (response.parsed_address?.street_name) {
            message += ` (${response.parsed_address.street_name})`;
          }

          this.showGeocodingMessage(message, 'success');
        } else {
          this.showGeocodingMessage('Auto-geocoding failed: No coordinates found', 'error');
        }

      } catch (error) {
        console.error('Error auto-geocoding address:', error);
        let errorMessage = 'Auto-geocoding failed. Disable auto-geocoding to enter coordinates manually.';

        // Handle specific error messages from the backend
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.data?.error) {
          errorMessage = error.response.data.error;
        }

        this.showGeocodingMessage(errorMessage, 'error');
      } finally {
        this.geocodingInProgress = false;
      }
    },

    showGeocodingMessage(message, type = 'info') {
      this.geocodingMessage = message;
      this.geocodingMessageType = type;

      // Auto-clear success messages after 8 seconds (longer for longer messages)
      if (type === 'success') {
        setTimeout(() => {
          if (this.geocodingMessageType === 'success') {
            this.clearGeocodingMessage();
          }
        }, 8000);
      }
    },

    async submitForm() {
      if (this.isSubmitting) return;

      if (!this.formIsValid) {
        alert('Please fill out all required fields: MPID, Street, City, and Customer Name.');
        return;
      }

      this.isSubmitting = true;

      try {
        const meteringPointData = {
          mpid: this.formData.mpid.trim(),
          street: this.formData.street.trim(),
          city: this.formData.city.trim(),
          zipCode: this.formData.zipCode?.trim() || null,
          customerFName: this.formData.customerFName.trim(),
          country: this.formData.country?.trim() || null,
          // Only include coordinates if they have actual values
          lat: this.formData.lat !== null && this.formData.lat !== '' ? Number(this.formData.lat) : null,
          lon: this.formData.lon !== null && this.formData.lon !== '' ? Number(this.formData.lon) : null,
        };

        const createdMpid = await MpidApiService.createMeteringPoint(meteringPointData);

        // Reset isSubmitting BEFORE emitting and closing
        this.isSubmitting = false;

        // Emit success event first
        this.$emit('success', createdMpid);

        // Then close the overlay - use $nextTick to ensure DOM updates
        this.$nextTick(() => {
          this.closeOverlay();
        });

      } catch (error) {
        console.error('Error creating metering point:', error);

        let errorMessage = 'An unexpected error occurred.';

        // Handle your Go backend's error response structure
        if (error.response?.data) {
          const errorData = error.response.data;

          // Your backend returns: { error_code, error, detail }
          if (errorData.detail) {
            errorMessage = errorData.detail;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }

          // Handle specific error codes for better UX
          if (errorData.error_code === 'ALREADY_EXISTS' || errorData.error === 'duplicate_mpid') {
            errorMessage = `A metering point with MPID "${this.formData.mpid}" already exists.`;
          } else if (errorData.error_code === 'VALIDATION_FAILED') {
            errorMessage = `Validation error: ${errorData.detail}`;
          } else if (errorData.error_code === 'AUTH_FAILED') {
            errorMessage = 'Authentication error. Please log in again.';
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert('Failed to create metering point:\n' + errorMessage);
        this.isSubmitting = false;
      }
    },

    closeOverlay() {
      if (this.geocodingDebounceTimer) {
        clearTimeout(this.geocodingDebounceTimer);
      }
      this.clearGeocodingMessage();
      this.wasGeocoded = false;
      // Emit close event to parent
      this.$emit('close');
    },

    beforeUnmount() {
      if (this.geocodingDebounceTimer) {
        clearTimeout(this.geocodingDebounceTimer);
      }
    }
  },
};
</script>


<style scoped>
/* Overlay */
.edit-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.edit-overlay-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to top, rgba(32, 32, 32, 0.1) 0%, rgba(16, 16, 16, 0.95) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease forwards;
}

.edit-overlay-content {
  position: relative;
  width: 700px;
  max-width: 90%;
  max-height: 90vh;
  border-radius: 8px;
  padding: 24px;
  color: white;
  background-color: rgba(50, 50, 50, 0.2);
  backdrop-filter: blur(16px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
  overflow-y: auto;
  animation: slideIn 0.3s ease forwards;
  z-index: 1001;
}

/* Header */
.edit-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  padding-bottom: 16px;
}

.edit-header h2 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin: 0;
}

.close-button {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 4px;
  transition: color 0.2s;
}

.close-button:hover {
  color: rgba(255, 255, 255, 0.9);
}

/* Form container */
.form-container {
  position: relative;
  min-height: 200px;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  background-color: rgba(0, 0, 0, 0.6);
  border-radius: 8px;
  gap: 16px;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Form sections */
.form-section {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.form-section:last-of-type {
  border-bottom: none;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 16px 0;
}

/* Form rows and groups */
.form-row {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 16px;
}

.form-group {
  flex: 1;
  min-width: 200px;
}

.form-label {
  display: block;
  font-size: 12px;
  font-weight: 500;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.7);
}

.form-input {
  width: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  color: white;
  padding: 8px 12px;
  font-size: 13px;
  transition: border-color 0.2s, background-color 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: rgba(79, 195, 247, 0.5);
  background-color: rgba(79, 195, 247, 0.05);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.field-hint {
  display: block;
  font-size: 10px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
}

/* Action buttons */
.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 16px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
}

.action-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.submit-button {
  background-color: rgba(79, 195, 247, 0.2);
}

.submit-button:hover:not(:disabled) {
  background-color: rgba(79, 195, 247, 0.3);
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 767px) {
  .form-row {
    flex-direction: column;
    gap: 16px;
  }
  .form-group {
    min-width: 100%;
  }
  .edit-actions {
    flex-direction: column-reverse;
    gap: 8px;
  }
  .action-button {
    width: 100%;
  }
}


.geocoding-toggle-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
}

.toggle-text {
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  cursor: pointer;
  user-select: none;
}

.toggle-checkbox {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.toggle-slider {
  position: relative;
  display: block;
  width: 44px;
  height: 24px;
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.toggle-slider::before {
  content: '';
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-checkbox:checked + .toggle-slider {
  background-color: rgba(79, 195, 247, 0.4);
  border-color: rgba(79, 195, 247, 0.6);
}

.toggle-checkbox:checked + .toggle-slider::before {
  transform: translateX(20px);
  background-color: #4fc3f7;
  box-shadow: 0 2px 6px rgba(79, 195, 247, 0.3);
}

.toggle-switch:hover .toggle-slider {
  background-color: rgba(255, 255, 255, 0.2);
}

.toggle-checkbox:checked + .toggle-slider:hover {
  background-color: rgba(79, 195, 247, 0.5);
}

.loading-indicator {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: rgba(79, 195, 247, 0.8);
  font-weight: 500;
  margin-left: auto;
}

.loading-icon {
  animation: spin 1s linear infinite;
}

.geocoding-info {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 16px;
  background-color: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.7);
  font-size: 11px;
  line-height: 1.5;
}

.geocoding-info svg {
  flex-shrink: 0;
  margin-top: 2px;
  opacity: 0.6;
}

.geocoding-info span {
  font-style: italic;
}

.geocoding-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 12px;
  font-weight: 500;
}

.geocoding-message.success {
  background-color: rgba(76, 175, 80, 0.1);
  border: 1px solid rgba(76, 175, 80, 0.3);
  color: rgba(76, 175, 80, 0.9);
}

.geocoding-message.error {
  background-color: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: rgba(244, 67, 54, 0.9);
}

.geocoding-message svg {
  flex-shrink: 0;
}

</style>